import { useState } from 'react';
import { SURVEY_SOURCES, type SurveyAction, type SurveyAnswers } from '../api/survey';
import { Button, FormField, LegalDialog, ResponsiveDialog } from '../components/ui';
import { useT } from '../i18n';
import styles from './SurveyCard.module.css';

const EMPTY: SurveyAnswers = { source: '', source_text: '', missing_text: '', paying_text: '' };
export interface SurveyCardProps {
  canSnooze: boolean;
  visible?: boolean;
  suspended?: boolean;
  preview?: boolean;
  onAnswer: (action: SurveyAction, answers: SurveyAnswers) => Promise<void>;
  onDone: () => void;
}

export function SurveyCard({ canSnooze, visible = true, suspended = false, preview = false, onAnswer, onDone }: SurveyCardProps) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [answers, setAnswers] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const hasAnswer = Object.values(answers).some(value => value.trim().length > 0);
  const act = async (action: SurveyAction) => {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    try { await onAnswer(action, answers); onDone(); }
    catch { setFailed(true); }
    finally { setBusy(false); }
  };
  const privacyNotice = <>
    <p>{t('survey.privacy')}</p>
    <button type="button" className={styles.link} onClick={() => setPrivacy(true)}>{t('survey.privacy_link')}</button>
  </>;
  const actions = <div className={styles.actions}>
    {canSnooze ? <Button variant="ghost" disabled={busy} onClick={() => void act('later')}>{t('survey.later')}</Button> : null}
    <Button variant="ghost" disabled={busy} onClick={() => void act('decline')}>{t('survey.decline')}</Button>
  </div>;
  return <>
    <div hidden={!visible || open} className={preview ? styles.preview : styles.dock} data-lorath-dock="survey">
      <section className={styles.card} aria-label={t('survey.title')}>
        <h2>{t('survey.title')}</h2>
        {preview ? <p>{t('survey.preview_note')}</p> : null}
        {privacyNotice}
        <div className={styles.actions}>
          <Button disabled={busy} onClick={() => setOpen(true)}>{t('survey.open')}</Button>
          {actions}
        </div>
        {failed ? <p role="alert" data-survey-error>{t('survey.failed')}</p> : null}
      </section>
    </div>
    <ResponsiveDialog open={open && !suspended} onClose={() => { if (!busy) setOpen(false); }} title={t('survey.title')} initialFocus="none">
      <form className={styles.form} onSubmit={event => { event.preventDefault(); if (hasAnswer) void act('submit'); }}>
        {preview ? <p>{t('survey.preview_note')}</p> : null}
        <p>{t('survey.optional')}</p>
        <FormField as="select" labelKey="survey.question_source" value={answers.source} disabled={busy}
          onChange={e => setAnswers(a => ({ ...a, source: e.target.value }))}>
          <option value="">{t('survey.choose')}</option>
          {SURVEY_SOURCES.map(source => <option key={source} value={source}>{t(`survey.source.${source}`)}</option>)}
        </FormField>
        {(['source_text', 'missing_text', 'paying_text'] as const).map(key => <FormField
          key={key} as="textarea" formatted={false} labelKey={`survey.question_${key}`} rows={3}
          value={answers[key]} maxLength={500} counter={{ value: answers[key].length, max: 500 }} disabled={busy}
          onChange={e => setAnswers(a => ({ ...a, [key]: e.target.value }))}
        />)}
        {privacyNotice}
        {failed ? <p role="alert" data-survey-error>{t('survey.failed')}</p> : null}
        <Button type="submit" disabled={busy || !hasAnswer}>{t('survey.submit')}</Button>
        {actions}
      </form>
    </ResponsiveDialog>
    <LegalDialog doc={privacy ? 'datenschutz' : null} onClose={() => setPrivacy(false)} />
  </>;
}
